<template>
    <div class="queue-screen">
        <header class="queue-header">
            <h1 class="queue-header__title">Queue</h1>
            <span class="queue-header__count" :class="{ 'queue-header__count--empty': !waitingCount }">
                {{ waitingCount }} waiting
            </span>
        </header>

        <main class="queue-main">
            <QueueList :autoProcess="autoSubmitForm" :list="queue"
                @send-email="(data, silent, trigger) => onSendLinkEmailClick(data, silent, trigger)"
                @delete="item => onDeleteFromQueue(item)" />
        </main>

        <nav class="queue-actionbar" aria-label="Add to queue">
            <CreateQueueButton class="js-queue-btn queue-actionbar__item" label="Add email" />
            <Scanner fluid template="badge" class="queue-actionbar__item js-scanner-btn"
                @scaned="(data) => onScannerScaned(data)" />
        </nav>

        <SendEmailForm @close="onFormClose" @submit="data => onFormSubmit(data)" :visible="formVisible"
            :hiddenFields="formHiddenFields" :autoSubmit="autoSubmitForm" :formDefaultData="formDefaultData"
            :processing="appStore.sendEmailLoading" />
        <ConfirmDialog />
    </div>
</template>

<script setup>
import $ from 'jquery';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/app';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import QueueService from './../services/queue.js'
import QueueList from '@/components/queue/QueueList.vue';
import SendEmailForm from '@/components/queue/SendEmailForm.vue';
import Scanner from '@/components/Scanner.vue';
import CreateQueueButton from '@/components/CreateQueueButton.vue';
import AutomateService from "@/services/automate";

const HIDDEN_FIELDS_MAP = {
    'button': [],
    'scanner': ['badge'],
};

const queue = ref();
const intervalId = ref();
const formDefaultData = ref({});
const formVisible = ref(false);
const formHiddenFields = ref([]);
// Set true if you want to auto submit form using scanner
const autoSubmitForm = ref(process.env.VUE_APP_AUTO_SUBMIT_FORM == '1');

const toast = useToast();
const confirm = useConfirm();
const appStore = useAppStore();

const waitingCount = computed(() => (Array.isArray(queue.value) ? queue.value.length : 0));

onMounted(() => {
    startInterval();
    handleSendEmailButton();
});

onUnmounted(() => {
    stopInterval();
});

const startInterval = () => {
    loadQueueList();
    intervalId.value = setInterval(loadQueueList, 5000);
};

const stopInterval = () => {
    clearInterval(intervalId.value);
};

const loadQueueList = () => {
    QueueService.getQueueAll('?status=todo').then(({ data }) => {
        queue.value = data
    });
};

const handleSendEmailButton = () => {
    $(document).on('click', '.js-queue-btn', (e) => {
        e.preventDefault;
        formVisible.value = true;
    });
};

const onDeleteFromQueue = (item) => {
    confirm.require({
        message: `Remove "${item.name || 'this entry'}" from the queue?`,
        header: 'Delete from queue',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Delete',
        rejectLabel: 'Cancel',
        accept: () => {
            QueueService.deleteFromQueue(item.id)
                .then(() => {
                    queue.value = queue.value.filter((row) => row.id !== item.id);
                    toast.add({ severity: 'success', summary: 'Removed from queue', life: 3000 });
                })
                .catch(({ response }) => {
                    const error = response?.data?.error || 'Failed to delete. Please try again.';
                    toast.add({ severity: 'error', summary: error, life: 3000 });
                });
        },
    });
};

const onSendLinkEmailClick = (data, silent = false, trigger = false) => {
    formHiddenFields.value = HIDDEN_FIELDS_MAP[trigger];
    formDefaultData.value = data;
    if (silent) {
        autoSubmitForm.value = true;
    } else {
        formVisible.value = true;
    }
};

const onFormClose = () => {
    formDefaultData.value = {};
    formVisible.value = false;
};

const onFormSubmit = (data) => {
    appStore.sendEmailLoading = true;
    QueueService.sendEmail(data)
        .then(response => {
            if (response.status != 200) {
                toast.add({ severity: 'error', summary: response.data.error, life: 3000 });
            } else {
                formDefaultData.value = {};
                formVisible.value = false;
                toast.add({ severity: 'success', summary: response.data.message, life: 3000 });
            }
            appStore.sendEmailLoading = false;
        })
        .catch((({ response }) => {
            toast.add({ severity: 'error', summary: response.data.error, life: 3000 });
            appStore.sendEmailLoading = false;
        }));
};

const onScannerScaned = async (scanedData) => {
    try {
        const { data: response } = await AutomateService.getBadgeInfo(scanedData);
        const formData = AutomateService.badgeInfoToEmailForm(response);
        formHiddenFields.value = ['badge'];
        formDefaultData.value = formData;
        formVisible.value = true;
    } catch (error) {
        toast.add({ severity: 'error', summary: 'Submission failed. Please try again.', life: 3000 });
    }
};

</script>

<style scoped>
.queue-screen {
    background: var(--surface-50, #f7f7f8);
}

.queue-header {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    padding: 0.9rem 1rem 0.75rem;
    background: var(--surface-0, #fff);
    border-bottom: 1px solid var(--surface-200);
}

.queue-header__title {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--text-color);
}

.queue-header__count {
    font-size: 0.9rem;
    font-weight: 700;
    color: #8a5a00;
    background: #fff3d6;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
}

.queue-header__count--empty {
    color: var(--surface-600);
    background: var(--surface-100);
}

.queue-main {
    padding: 0.25rem 1rem 0;
}

.queue-actionbar {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: var(--surface-0, #fff);
    border-top: 1px solid var(--surface-200);
}

.queue-actionbar__item {
    flex: 1 1 0;
    min-width: 0;
}

/* Phone: full-bleed shell with a sticky header and a thumb-zone action bar
   pinned to the bottom. Scoped to mobile so the tablet/booth layout (which
   has its own fixed top-bar + layout container) isn't overlapped by a fixed
   bar spanning the whole viewport. */
@media (max-width: 576px) {
    .queue-screen {
        min-height: 100vh;
        /* dvh keeps the action bar above the mobile browser chrome */
        min-height: 100dvh;
    }

    .queue-header {
        position: sticky;
        top: 0;
        z-index: 10;
        padding-top: calc(env(safe-area-inset-top) + 0.9rem);
    }

    .queue-main {
        /* Clear the fixed action bar (height + safe area) so the last row shows */
        padding-bottom: calc(env(safe-area-inset-bottom) + 6rem);
    }

    .queue-actionbar {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 20;
        padding-bottom: calc(env(safe-area-inset-bottom) + 0.75rem);
        box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
    }
}

/* Both bottom actions share one shape (height, radius, icon/label size) but
   keep the app-wide colour logic: Add email = yellow (its own style), Scan =
   theme purple. So colour is intentionally not set here. */
.queue-actionbar :deep(.p-button) {
    min-height: 3.25rem;
    border-radius: 0.75rem;
    gap: 0.6rem;
    font-size: 1.0625rem;
    font-weight: 700;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    transition: transform 0.15s ease, filter 0.15s ease;
}

.queue-actionbar :deep(.p-button:active) {
    transform: translateY(1px);
    filter: brightness(0.95);
}

/* Normalise both icons to one size (overrides the icon-only button's 2rem). */
.queue-actionbar :deep(.p-button .pi)::before {
    font-size: 1.3rem !important;
    font-weight: 900;
}

/* Normalise both labels to one size/weight. */
.queue-actionbar :deep(.create-queue-btn__label),
.queue-actionbar :deep(.p-button > span) {
    font-size: 1.0625rem;
    font-weight: 700;
}
</style>